import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Users, FileText, Headphones, Building2, UserCog, Monitor, Shield, Clock, TrendingDown, Award, Search, Globe, Target, BarChart3, UserCheck } from "lucide-react";
import JsonLd, { organizationSchema, websiteSchema, localBusinessSchema, buildFAQSchema, buildBreadcrumbSchema } from "@/components/JsonLd";

const SERVICES = [
  { icon: FileText, name: "Data Entry & Processing", desc: "Accurate, high-volume data entry, extraction, and processing services that reduce errors and free up your core team.", path: "/services/data-entry", color: "bg-blue-50 text-blue-600" },
  { icon: Building2, name: "Accounts Payable / Receivable", desc: "End-to-end AP/AR management including invoice processing, payment tracking, reconciliation, and reporting.", path: "/services/accounts-payable-receivable", color: "bg-emerald-50 text-emerald-600" },
  { icon: Shield, name: "Back Office Support", desc: "Administrative and operational support that keeps your business running efficiently behind the scenes.", path: "/services/back-office-support", color: "bg-violet-50 text-violet-600" },
  { icon: Headphones, name: "Customer Support", desc: "24/7 multichannel customer service with trained agents who represent your brand professionally.", path: "/services/customer-support", color: "bg-amber-50 text-amber-600" },
  { icon: UserCog, name: "HR Outsourcing", desc: "Recruitment, onboarding, payroll administration, compliance, and employee lifecycle management.", path: "/services/hr-outsourcing", color: "bg-rose-50 text-rose-600" },
  { icon: Monitor, name: "IT Outsourcing", desc: "Technical support, infrastructure management, cloud services, and software development teams.", path: "/services/it-outsourcing", color: "bg-cyan-50 text-cyan-600" },
  { icon: UserCheck, name: "Virtual Employee", desc: "Hire dedicated remote professionals who work exclusively for your business. Pre-vetted and ready in 48 hours.", path: "/services/virtual-employee", color: "bg-lime-50 text-lime-600" },
];

const DIGITAL_SERVICES = [
  { icon: Search, name: "SEO Services", desc: "Dominate search results with proven SEO strategies. Keyword research, technical SEO, link building, and local SEO.", path: "/services/seo", color: "bg-orange-50 text-orange-600" },
  { icon: Globe, name: "Web Development", desc: "Stunning, high-performance websites built to convert visitors into customers. Custom design and development.", path: "/services/web-development", color: "bg-indigo-50 text-indigo-600" },
  { icon: Target, name: "PPC Advertising", desc: "Data-driven Google Ads and social media advertising campaigns that deliver qualified leads and ROI.", path: "/services/ppc", color: "bg-pink-50 text-pink-600" },
  { icon: BarChart3, name: "Social Media Marketing", desc: "Strategic social media management, content creation, and growth campaigns across all platforms.", path: "/services/social-media", color: "bg-teal-50 text-teal-600" },
];

const PROCESS_STEPS = [
  { step: "01", title: "Consultation", desc: "We assess your business needs, current processes, and identify the highest-impact areas for outsourcing." },
  { step: "02", title: "Integration", desc: "Our team integrates seamlessly with your workflows, systems, and communication channels." },
  { step: "03", title: "Execution", desc: "Dedicated professionals handle your operations with precision, quality controls, and regular reporting." },
  { step: "04", title: "Optimisation", desc: "Continuous process improvement, analytics, and scaling to drive increasing efficiency and cost savings." },
];

const STATS = [
  { value: "500+", label: "Clients Served" },
  { value: "50-70%", label: "Cost Savings" },
  { value: "99.5%", label: "Accuracy Rate" },
  { value: "24/7", label: "Support Available" },
];

const TESTIMONIALS = [
  { text: "Adsolute transformed our back-office operations. We reduced processing costs by 60% while improving accuracy. Their team feels like an extension of our own.", author: "Sarah Mitchell", role: "CFO, Mitchell Logistics", avatar: "https://images.unsplash.com/photo-1573497491207-618cc224f243?w=96&h=96&fit=crop&crop=face" },
  { text: "The customer support team Adsolute provided has been outstanding. Our satisfaction scores increased by 35% within the first quarter.", author: "James Chen", role: "Operations Director, TechFlow", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=96&h=96&fit=crop&crop=face" },
  { text: "We moved our AP/AR processing to Adsolute and haven't looked back. Faster turnaround, fewer errors, and our finance team can finally focus on strategy.", author: "Emily Thompson", role: "Finance Director, GreenVista", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=96&h=96&fit=crop&crop=face" },
];

const Home = () => {
  return (
    <main data-testid="home-page">
      <Helmet>
        <title>Adsolute | Business Process Outsourcing Services UK</title>
        <meta name="description" content="Adsolute provides expert BPO services including data entry, accounts payable/receivable, back office support, customer support, HR and IT outsourcing. Save 50-70% on operational costs." />
      </Helmet>
      <JsonLd data={organizationSchema} />
      <JsonLd data={websiteSchema} />
      <JsonLd data={localBusinessSchema} />
      <JsonLd data={buildBreadcrumbSchema([{ name: "Home", path: "/" }])} />
      <JsonLd data={buildFAQSchema([
        { q: "What is business process outsourcing?", a: "Business process outsourcing (BPO) is the practice of contracting specific business operations and tasks to a third-party provider. Adsolute provides BPO services including data entry, accounts payable/receivable, back office support, customer service, HR and IT outsourcing." },
        { q: "How much can I save with BPO?", a: "Most businesses save 50-70% on operational costs by outsourcing to Adsolute. Savings come from reduced overhead, no recruitment costs, flexible scaling, and access to specialist expertise without full-time salaries." },
        { q: "How quickly can you set up a BPO team?", a: "We can typically have a dedicated team operational within 1-2 weeks. Our onboarding process covers systems integration, training, and quality assurance to ensure a seamless transition." },
        { q: "Do you work with UK businesses?", a: "Yes, Adsolute is a UK-based BPO provider serving businesses across the United Kingdom and internationally. Our team operates from our offices in Camberley, Surrey." },
        { q: "What industries do you serve?", a: "We serve businesses across all industries including finance, healthcare, logistics, retail, technology, legal, and professional services." },
      ])} />

      {/* ===== HERO ===== */}
      <section className="hero-gradient pt-28 pb-20 lg:pt-36 lg:pb-28 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-sm uppercase tracking-widest text-[#0047AB] font-semibold mb-4 block" data-testid="hero-label">Business Process Outsourcing</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-tight text-[#0F172A] mb-6 heading-font" data-testid="hero-title">
                Scale Your Business.<br /><span className="text-gradient">Reduce Your Costs.</span>
              </h1>
              <p className="text-lg text-slate-600 max-w-xl mb-8 leading-relaxed">
                Adsolute provides expert outsourcing services that help UK businesses save 50-70% on operational costs while improving accuracy and efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                  Get a Free Consultation <ArrowRight className="w-5 h-5" />
                </Link>
                <a href="#services" className="btn-secondary" data-testid="hero-cta-secondary">
                  Explore Services
                </a>
              </div>
              {/* Trust indicators */}
              <div className="flex items-center gap-6 mt-10 pt-8 border-t border-slate-200">
                {STATS.map((s, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl font-bold text-[#0F172A] heading-font">{s.value}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative hidden lg:block">
              <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop&q=80" alt="Professional business team meeting" className="rounded-lg shadow-2xl w-full" width="800" height="600" fetchPriority="high" />
              {/* Floating stats card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-xl p-5 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><TrendingDown className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <p className="text-sm text-slate-500">Cost Reduction</p>
                    <p className="text-xl font-bold text-[#0F172A] heading-font">Up to 70%</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm uppercase tracking-widest text-[#0047AB] font-semibold">Our Services</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mt-3 heading-font">
              Comprehensive Outsourcing Solutions
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">Discover how our tailored BPO services can streamline your operations, reduce costs, and give you the freedom to focus on growth.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((svc, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to={svc.path} className="service-card p-6 block h-full group" data-testid={`service-card-${i}`}>
                  <div className={`w-12 h-12 rounded-lg ${svc.color} flex items-center justify-center mb-4`}>
                    <svc.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2 group-hover:text-[#0047AB] transition-colors">{svc.name}</h3>
                  <p className="text-sm text-slate-500 mb-4 leading-relaxed">{svc.desc}</p>
                  <span className="text-sm font-medium text-[#0047AB] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DIGITAL MARKETING SERVICES ===== */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm uppercase tracking-widest text-[#0047AB] font-semibold">Digital Marketing</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mt-3 heading-font">
              Grow Your Online Presence
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">Complement your outsourcing with our expert digital marketing services to drive traffic, generate leads, and build your brand online.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {DIGITAL_SERVICES.map((svc, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to={svc.path} className="service-card p-6 block h-full group" data-testid={`dm-card-${i}`}>
                  <div className={`w-12 h-12 rounded-lg ${svc.color} flex items-center justify-center mb-4`}>
                    <svc.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2 group-hover:text-[#0047AB] transition-colors">{svc.name}</h3>
                  <p className="text-sm text-slate-500 mb-4 leading-relaxed">{svc.desc}</p>
                  <span className="text-sm font-medium text-[#0047AB] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm uppercase tracking-widest text-[#0047AB] font-semibold">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mt-3 heading-font">
              Simple 4-Step Process
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {PROCESS_STEPS.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center process-connector" data-testid={`process-step-${i}`}>
                <div className="w-16 h-16 rounded-full bg-[#EFF6FF] text-[#0047AB] flex items-center justify-center mx-auto mb-4 text-xl font-bold heading-font">{step.step}</div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{step.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY ADSOLUTE ===== */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src="https://images.unsplash.com/photo-1624555130581-1d9cca783bc0?w=700&h=500&fit=crop&q=80" alt="Professional team collaboration" className="rounded-lg shadow-lg w-full" width="700" height="500" loading="lazy" decoding="async" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-sm uppercase tracking-widest text-[#0047AB] font-semibold">Why Adsolute</span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mt-3 mb-6 heading-font">
                Your Trusted Outsourcing Partner
              </h2>
              <div className="space-y-4">
                {[
                  { icon: Award, text: "UK-based with dedicated project managers and quality assurance" },
                  { icon: Clock, text: "24/7 operations with flexible scaling up or down" },
                  { icon: TrendingDown, text: "Save 50-70% compared to in-house teams" },
                  { icon: Shield, text: "ISO-standard processes with data security compliance" },
                  { icon: Users, text: "Pre-vetted, trained professionals ready in under 2 weeks" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-md bg-[#EFF6FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-[#0047AB]" />
                    </div>
                    <p className="text-slate-600">{item.text}</p>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn-primary mt-8 inline-flex" data-testid="why-cta">
                Start Saving Today <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm uppercase tracking-widest text-[#0047AB] font-semibold">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mt-3 heading-font">What Our Clients Say</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="service-card p-6" data-testid={`testimonial-${i}`}>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover" width="40" height="40" loading="lazy" decoding="async" />
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{t.author}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="section-padding cta-gradient relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6 heading-font">
              Ready to Transform Your Operations?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Get a free consultation and discover how Adsolute can help you reduce costs, improve efficiency, and scale your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-[#0047AB] font-semibold px-8 py-4 rounded-md hover:bg-blue-50 transition-colors inline-flex items-center gap-2" data-testid="cta-primary">
                Schedule Free Consultation <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="tel:+447481111540" className="border-2 border-white text-white font-semibold px-8 py-4 rounded-md hover:bg-white/10 transition-colors inline-flex items-center gap-2" data-testid="cta-phone">
                Call +44 748 1111 540
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Home;
